import * as React from 'react'
import { motion } from 'framer-motion'
import { cn } from '../../utils/cn'

/**
 * JPG Academy Search Input
 *
 * Text input with a leading search icon, optional clear button,
 * and an optional "showing X of Y" label. Replaces 4+ duplicated
 * search implementations across the app.
 *
 * Usage:
 *   <SearchInput placeholder="Search employees…" onSearch={setQuery} />
 *   <SearchInput value={q} onSearch={setQ} onClear={() => setQ('')} showingLabel="3 of 12" />
 */

export interface SearchInputProps
	extends Omit<React.InputHTMLAttributes<HTMLInputElement>, 'size' | 'onChange'> {
	/** Called when the input value changes */
	onSearch?: (value: string) => void
	/** Called when the clear button is clicked */
	onClear?: () => void
	/** Size variant */
	inputSize?: 'sm' | 'md'
	/** Optional "showing X of Y" label displayed to the right */
	showingLabel?: string
	className?: string
}

const sizeStyles = {
	sm: 'h-8 text-xs pl-8 pr-8',
	md: 'h-10 text-sm pl-10 pr-10',
}

const iconSizes = {
	sm: 'w-3.5 h-3.5 left-2.5',
	md: 'w-4 h-4 left-3',
}

export const SearchInput = React.forwardRef<HTMLInputElement, SearchInputProps>(
	(
		{
			className,
			onSearch,
			onClear,
			inputSize = 'md',
			showingLabel,
			value,
			...props
		},
		ref,
	) => {
		const [internalValue, setInternalValue] = React.useState('')
		const controlled = value !== undefined
		const displayValue = controlled ? String(value) : internalValue
		const hasValue = displayValue.length > 0

		const handleChange = React.useCallback(
			(e: React.ChangeEvent<HTMLInputElement>) => {
				const v = e.target.value
				if (!controlled) setInternalValue(v)
				onSearch?.(v)
			},
			[controlled, onSearch],
		)

		const handleClear = React.useCallback(() => {
			if (!controlled) setInternalValue('')
			onSearch?.('')
			onClear?.()
		}, [controlled, onSearch, onClear])

		return (
			<motion.div
				className={cn('relative flex items-center gap-2', className)}
				initial={{ opacity: 0, y: 4 }}
				animate={{ opacity: 1, y: 0 }}
				transition={{ duration: 0.2, ease: [0.4, 0, 0.2, 1] }}
			>
				<div className="relative flex-1">
					{/* Search icon */}
					<svg
						className={cn(
							'absolute top-1/2 -translate-y-1/2 text-muted-foreground pointer-events-none',
							iconSizes[inputSize],
						)}
						viewBox="0 0 24 24"
						fill="none"
						stroke="currentColor"
						strokeWidth="2"
						strokeLinecap="round"
						strokeLinejoin="round"
					>
						<circle cx="11" cy="11" r="8" />
						<path d="m21 21-4.3-4.3" />
					</svg>

					<input
						ref={ref}
						type="search"
						value={displayValue}
						onChange={handleChange}
						className={cn(
							'w-full font-ui rounded-md border transition-all duration-fast ease-default',
							'bg-background text-foreground placeholder:text-muted-foreground',
							'focus:outline-none focus:ring-2 focus:ring-gold/30 focus:border-gold',
							'border-input hover:border-navy-300',
							sizeStyles[inputSize],
						)}
						{...props}
					/>

					{/* Clear button */}
					{hasValue && (
						<button
							type="button"
							onClick={handleClear}
							className={cn(
								'absolute top-1/2 -translate-y-1/2 p-1 rounded-md',
								'text-muted-foreground hover:text-foreground transition-colors',
								inputSize === 'sm' ? 'right-1.5' : 'right-2',
							)}
							aria-label="Clear search"
						>
							<svg
								className="w-3.5 h-3.5"
								viewBox="0 0 24 24"
								fill="none"
								stroke="currentColor"
								strokeWidth="2"
								strokeLinecap="round"
								strokeLinejoin="round"
							>
								<path d="M18 6 6 18" />
								<path d="m6 6 12 12" />
							</svg>
						</button>
					)}
				</div>

				{showingLabel && (
					<span className="text-xs text-muted-foreground whitespace-nowrap font-ui">
						{showingLabel}
					</span>
				)}
			</motion.div>
		)
	},
)

SearchInput.displayName = 'SearchInput'
